/**
 * 导航与页面清单
 * ---------------------------------------------------------------------------
 * 侧栏分组、路由地址、页面标题全部集中在这里，是整个应用的目录。
 * 加页面 = 在这里加一条 + 在 routes.tsx 里挂上组件，两处对齐即可。
 *
 * 结构对应设计稿 §04 老板端画布的侧栏（每天使用 / 经营管理 / 系统）与
 * 产品文档 §7 信息架构。
 */
import type { IconName } from '@/components/icons';

export type NavItem = {
  /** 路由路径，同时作为侧栏选中判断的依据 */
  path: string;
  /** 侧栏文案 */
  label: string;
  icon: IconName;
  /** 页面标题（顶栏大标题） */
  title: string;
  /** 页面副标题，一句话说清这一页解决什么问题 */
  subtitle: string;
  /**
   * 进入该页需要的权限码。没有这个权限时侧栏不显示该项，
   * 直接输地址进去也会看到"没有权限"而不是空白页。
   */
  permission?: string;
  /**
   * 该页依赖的功能开关（门店设置里的 featureCode）。
   * 基础模式下没开的功能，侧栏里会显示成"未开启"而不是直接消失——
   * 让老板知道有这个能力，而不是永远发现不了（产品文档 §5.5）。
   */
  feature?: string;
};

export type NavGroup = {
  title: string;
  items: NavItem[];
};

export const NAV_GROUPS: NavGroup[] = [
  {
    title: '每天使用',
    items: [
      {
        path: '/dashboard',
        label: '工作台',
        icon: 'home',
        title: '工作台',
        subtitle: '今天的经营情况一屏看完',
      },
      {
        path: '/checkout',
        label: '快速开单',
        icon: 'plus',
        title: '快速开单',
        subtitle: '选顾客、选项目、收款，三步完成',
        permission: 'order.create',
      },
      {
        path: '/orders',
        label: '订单',
        icon: 'receipt',
        title: '订单',
        subtitle: '每一笔消费、退款和作废都有记录',
        permission: 'order.read',
      },
      {
        path: '/members',
        label: '会员',
        icon: 'users',
        title: '会员',
        subtitle: '会员资料、储值和消费历史统一管理',
        permission: 'member.read',
      },
      {
        path: '/appointments',
        label: '预约与排队',
        icon: 'clock',
        title: '预约与排队',
        subtitle: '看清今天谁有空、顾客等多久',
        permission: 'appointment.read',
        feature: 'appointments',
      },
    ],
  },
  {
    title: '经营管理',
    items: [
      {
        path: '/staff',
        label: '员工与工资',
        icon: 'staff',
        title: '员工与工资',
        subtitle: '业绩、提成和工资来源一笔一笔算清楚',
        permission: 'staff.read',
      },
      {
        path: '/commissions',
        label: '提成规则',
        icon: 'percent',
        title: '提成规则',
        subtitle: '服务、助理、充值、商品和套餐的计算方式集中管理',
        permission: 'commission.rule.read',
      },
      {
        path: '/catalog',
        label: '项目与商品',
        icon: 'scissors',
        title: '项目与商品',
        subtitle: '管理门店价目表、商品和会员套餐',
        permission: 'catalog.read',
      },
      {
        path: '/marketing',
        label: '活动与优惠券',
        icon: 'ticket',
        title: '活动与优惠券',
        subtitle: '创建活动、发券、开单核销并统计实际效果',
        permission: 'marketing.read',
        feature: 'marketing',
      },
      {
        path: '/inventory',
        label: '库存',
        icon: 'box',
        title: '库存',
        subtitle: '商品进出、盘点和预警都有流水',
        permission: 'inventory.read',
        feature: 'inventory',
      },
      {
        path: '/finance',
        label: '账本与对账',
        icon: 'ledger',
        title: '账本与对账',
        subtitle: '区分收款、收入、充值和会员消耗',
        permission: 'finance.read',
      },
      {
        path: '/reports',
        label: '经营报表',
        icon: 'chart',
        title: '经营报表',
        subtitle: '不用懂财务，也能看懂门店经营',
        permission: 'report.read',
      },
    ],
  },
  {
    title: '系统',
    items: [
      {
        path: '/support',
        label: '售后工单',
        icon: 'headset',
        title: '售后工单',
        subtitle: '遇到问题在这里报给我们，处理进度也在这里看',
        permission: 'support_ticket.manage',
      },
      {
        path: '/support-access',
        label: '支持授权',
        icon: 'lock',
        title: '平台支持授权',
        subtitle: '由你确认平台排障范围、有效期和撤销状态',
        permission: 'support_session.approve',
      },
      {
        path: '/billing',
        label: '订阅与账单',
        icon: 'sliders',
        title: '订阅与账单',
        subtitle: '订阅、短信额度与订单，都是向平台付费的事',
        permission: 'settings.read',
      },
      {
        path: '/settings',
        label: '门店设置',
        icon: 'gear',
        title: '门店设置',
        subtitle: '门店资料、能力开关、权限、支付和小票设置',
        permission: 'settings.read',
      },
      {
        path: '/devices',
        label: '设备与打印',
        icon: 'print',
        title: '设备与打印',
        subtitle: '连接 USB、WiFi 小票机和 A4 打印机',
      },
      {
        path: '/deployment',
        label: '部署方案',
        icon: 'cloud',
        title: '部署方案',
        subtitle: '云端统一数据，Windows / macOS 桌面客户端负责收银与硬件',
      },
    ],
  },
];

/** 打平后的页面清单，按路径查页面信息时用 */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/** 按当前路由取页面标题；找不到时返回 null（比如 404） */
export function findNavItem(pathname: string): NavItem | null {
  // 用前缀匹配，让 /members/:id 这类详情页也能带出"会员"的标题
  return (
    NAV_ITEMS.find((item) => pathname === item.path) ??
    NAV_ITEMS.find((item) => pathname.startsWith(`${item.path}/`)) ??
    null
  );
}
